// Finds the city the person is in, as privately as possible:
// - the browser asks for location permission first;
// - the position is rounded to ~100 m before it leaves the device and is only
//   sent to Mapbox (our map provider), never to Frolik's server;
// - the result is the city and the city's own centre point, not the person's
//   position, and it's only used if they confirm it.

import { placeFromMapboxFeature } from './eventPlace';

const MAPBOX_URL = 'https://api.mapbox.com/geocoding/v5/mapbox.places';

function getPosition() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Your browser can’t share its location. Please type a location.'));
      return;
    }
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: false,
      timeout: 10000,
      maximumAge: 10 * 60 * 1000,
    });
  });
}

export async function getApproximateCity(mapboxToken) {
  if (!mapboxToken) {
    throw new Error('Location lookup isn’t available. Please type a location.');
  }

  let position;
  try {
    position = await getPosition();
  } catch (error) {
    throw new Error(
      error && error.code === 1
        ? 'Location access was blocked. Please type a location.'
        : 'Couldn’t find where you are. Please type a location.'
    );
  }

  // ~100 m precision: 3 decimal places.
  const latitude = Number(position.coords.latitude.toFixed(3));
  const longitude = Number(position.coords.longitude.toFixed(3));

  const response = await fetch(
    `${MAPBOX_URL}/${longitude},${latitude}.json?types=place&limit=1&access_token=${mapboxToken}`
  );
  const data = await response.json().catch(() => ({}));
  const city = Array.isArray(data.features) ? data.features[0] : null;

  if (!response.ok || !city) {
    throw new Error('Couldn’t work out your city. Please type a location.');
  }

  return placeFromMapboxFeature(city);
}
